import React from 'react';
import * as IoC from 'typescript-ioc';
import { IZoomUser } from '../../../api/zoom.dao';
import DefaultButton from '../../global/default-button/default-button';
import { Link } from '../../../infrastructure/components/router-extensions';
import { RouteParams, Routes } from '../../../routes-table';
import { IRouter } from '../../../infrastructure/router';
import { connectZoomVM } from '../zoom.context';
import { safeUseState } from '../../../infrastructure/hooks/safe-use-state';

interface IZoomManagementActionsAreaProps {
    confirmAction: () => Promise<void>;
    pendingAddUsers: Readonly<IZoomUser[]>;
    pendingAddUsersLength: number;
    pendingRemoveUsers: Readonly<string[]>;
    pendingRemoveUsersLength: number;
    updateTrackedUsersAPIError: string;
}

const ZoomManagementActionAreaDisconnected = (props: IZoomManagementActionsAreaProps) => {
    const [isBeingSaved, setIsBeingSaved] = safeUseState(false);

    const isUpdateActive = props.pendingAddUsersLength || props.pendingRemoveUsersLength;

    const router = IoC.Container.get<IRouter<RouteParams>>(IRouter);

    const applySelectionAction = async () => {
        if (isUpdateActive && !isBeingSaved) {
            try {
                setIsBeingSaved(true);
                await props.confirmAction();
                router.navigate(Routes.TrackedZoomUsers, {});
            } finally {
                setIsBeingSaved(false);
            }
        }
    };

    const saveButtonText = !isBeingSaved ? 'Apply Selection' : 'Selection is being applied...';

    const { pendingAddUsersLength, pendingRemoveUsersLength, updateTrackedUsersAPIError } = props;

    return (
        <div className='zoom-actions buttons-block'>
            {updateTrackedUsersAPIError && <div className='zoom-actions__save-error'>
                API Error: {updateTrackedUsersAPIError}. <br />
                Actual information has been synchronized with server after error appears. <br />
                Please, re-apply your changes if they were lost.
            </div>}
            {isUpdateActive
                ? <DefaultButton text={saveButtonText} modifier='fat' onClick={applySelectionAction} />
                : (
                    <Link routeEnum={Routes.TrackedZoomUsers}>
                        <DefaultButton text='Back to tracked users list' modifier='fat'/>
                    </Link>
                )
            }
            {pendingAddUsersLength !== 0 && (
                <div className='zoom-actions__start-count-block'>
                    Begin tracking for {pendingAddUsersLength} users
                </div>
            )}
            {pendingRemoveUsersLength !== 0 && (
                <div className='zoom-actions__stop-count-block'>
                    Stop tracking of {pendingRemoveUsersLength} users
                </div>
            )}
        </div>
    );
};

const ZoomManagementActionArea = connectZoomVM<IZoomManagementActionsAreaProps>(
    ZoomManagementActionAreaDisconnected, zoomVM => {
    const usersManagementVM = zoomVM.getUsersManagementVM();

    return {
        pendingAddUsers: usersManagementVM.getAddedUsers(),
        pendingAddUsersLength: usersManagementVM.getAddedUsers().length,
        pendingRemoveUsers: usersManagementVM.getRemovedUsers(),
        pendingRemoveUsersLength: usersManagementVM.getRemovedUsers().length,
        updateTrackedUsersAPIError: usersManagementVM.getSaveChangesError(),
        confirmAction: usersManagementVM.saveUserManagementChanges
    };
});

export { ZoomManagementActionArea };
